import { Transformation } from 'app/model/dataflow/transformation';
import { CallTransformation } from 'app/model/dataflow/calltransformation';
import { CallPlainObject } from 'worker/app-workers/shared/plainObjects/call.plainObject';
import { SelectTransformation } from 'app/model/dataflow/selectTransformation';
import { AdapterContext, IResourceContext, Metamodel, Owned } from 'shell-models-lib';
import { ColumnMappingConvertor } from './mapping.convertor';
import { ColumnMappingBaseTransformPlainObject } from 'worker/app-workers/shared/plainObjects/columnMappingBase.plainObject';
import { BaseColumnMappingTransformation } from 'app/model/dataflow/base-column-mapping-transformation';
import { DataFlowExpressionUtils } from '../../expression.util';
import { RestCallSettings } from 'app/model/dataflow/dataset-settings/restCall-settings';
import { RestCallFormatAdapter } from '../../formatAdapters/restCall.adapter';
import { TransformConvertorBase } from './transform.convertor';
import { SchemaConvertor } from './schema.convertor';
import { BaseSqlFormatAdapter } from 'app/model/adapters/dataflow/formatAdapters/sql-base.adapter';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { BooleanDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { DataflowFormatRegistryService } from 'app/registries/dataflow/dataflow-format-registry.service';
import {
    ExpressionColumnPatternPlainObject,
    ExpressionColumnPlainObject
} from 'worker/app-workers/shared/plainObjects/expressionColumn.plainObject';
import { DataFlowSchemaAdapterUtils } from '../../dataflow-schema-adapter-utils';
import { BaseSchemaModifierTransformation } from 'app/model/dataflow/base-schema-modifier-transformation';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export class CallConvertor extends TransformConvertorBase {
    private get callTransform(): CallTransformation {
        return this.transformation as CallTransformation;
    }

    private get transformBase(): BaseSchemaModifierTransformation {
        return this.transformation as BaseSchemaModifierTransformation;
    }

    public async convert(contextData: IResourceContext): Promise<Transformation> {
        await super.convert(contextData);
        const plainObject = this.transformObject as CallPlainObject;

        this.callTransform.source.name = this.callTransform.dataflow.uniqueName(DataFlowSource.MODEL);
        this.callTransform.source.isInlineDataset = true;

        // Convert the map column
        if (plainObject) {
            this.callTransform.selectTx = Owned.create<SelectTransformation>(
                SelectTransformation.MODEL,
                this.callTransform
            );
            this.callTransform.selectTx.name = this.callTransform.dataflow.uniqueName(Transformation.MODEL);
            ColumnMappingConvertor.convertFromPlainObject(
                plainObject as ColumnMappingBaseTransformPlainObject,
                this.callTransform.selectTx as BaseColumnMappingTransformation,
                this._handledConfigs
            );
        }

        // retrieve settings with expression builder
        plainObject.columns.forEach((settingsParameter) => {
            plainObject.configs.set(
                (settingsParameter as ExpressionColumnPlainObject).name,
                (settingsParameter as ExpressionColumnPlainObject).expression
            );
        });
        // Convert the configs
        for (const [key, value] of plainObject.configs.entries()) {
            if (key === 'store') {
                const storeType = DataFlowExpressionUtils.extractStringConfigValue(value);

                if (storeType === 'restservice') {
                    const adapter = AdapterContext.defaultContextOf(contextData).dataflowDSLAdapterContext.adapterFor(
                        RestCallSettings.MODEL
                    ) as RestCallFormatAdapter;
                    let settings = adapter.toTransformModel(
                        this.callTransform,
                        plainObject.configs,
                        this._handledConfigs
                    );

                    // Convert the column
                    if (plainObject.outputDefinition) {
                        let bodyCol = plainObject.outputDefinition.find((col) => col.name === 'body');
                        if (bodyCol) {
                            this.callTransform.includeBody = true;
                            this.callTransform.staticBodyColumn = SchemaConvertor.convertColumnFromPlainObject(
                                bodyCol,
                                this.callTransform
                            );
                        } else if (plainObject.configs.has('bodyColumnName')) {
                            this.callTransform.includeBody = true;
                            bodyCol = plainObject.outputDefinition.find(
                                (col) =>
                                    col.name ===
                                    plainObject.configs
                                        .get('bodyColumnName')
                                        .substring(1, plainObject.configs.get('bodyColumnName').length - 1)
                            );
                            if (bodyCol) {
                                this.callTransform.staticBodyColumn = SchemaConvertor.convertColumnFromPlainObject(
                                    bodyCol,
                                    this.callTransform
                                );
                            } else {
                                settings.responseBodyColName.expression = plainObject.configs
                                    .get('bodyColumnName')
                                    .substring(1, plainObject.configs.get('bodyColumnName').length - 1);
                                this.callTransform.staticBodyColumn = Owned.create<DataFlowColumn>(
                                    DataFlowColumn.MODEL,
                                    this.callTransform
                                );
                                this.callTransform.staticBodyColumn.name = settings.responseBodyColName.expression;
                            }
                        } else {
                            this.callTransform.includeBody = false;
                        }

                        if (
                            this.callTransform.staticBodyColumn &&
                            this.callTransform.staticBodyColumn.dataFlowColumnType
                        ) {
                            this.callTransform.staticBodyDataFlowExpression.expression =
                                this.callTransform.staticBodyColumn.dataFlowColumnType.typeInDsl;
                            this.callTransform.staticBodyDataFlowExpression.returnType =
                                this.callTransform.staticBodyColumn.dataFlowColumnType;
                        }

                        let headerCol = plainObject.outputDefinition.find((col) => col.name === 'headers');
                        if (headerCol) {
                            this.callTransform.includeHeader = true;
                            this.callTransform.headerColumn = SchemaConvertor.convertColumnFromPlainObject(
                                headerCol,
                                this.callTransform
                            );
                        } else if (
                            plainObject.configs.has('headerColumnName') &&
                            (headerCol = plainObject.outputDefinition.find(
                                (col) =>
                                    col.name ===
                                    plainObject.configs
                                        .get('headerColumnName')
                                        .substring(1, plainObject.configs.get('headerColumnName').length - 1)
                            ))
                        ) {
                            this.callTransform.includeHeader = true;
                            this.callTransform.headerColumn = SchemaConvertor.convertColumnFromPlainObject(
                                headerCol,
                                this.callTransform
                            );
                        } else {
                            this.callTransform.includeHeader = false;
                        }

                        let statusCol = plainObject.outputDefinition.find((col) => col.name === 'status');
                        if (statusCol) {
                            this.callTransform.includeStatus = true;
                            this.callTransform.statusColumn = SchemaConvertor.convertColumnFromPlainObject(
                                statusCol,
                                this.callTransform
                            );
                        } else if (
                            plainObject.configs.has('statusColumnName') &&
                            (statusCol = plainObject.outputDefinition.find(
                                (col) =>
                                    col.name ===
                                    plainObject.configs
                                        .get('statusColumnName')
                                        .substring(1, plainObject.configs.get('statusColumnName').length - 1)
                            ))
                        ) {
                            this.callTransform.includeStatus = true;
                            this.callTransform.statusColumn = SchemaConvertor.convertColumnFromPlainObject(
                                statusCol,
                                this.callTransform
                            );
                        } else {
                            this.callTransform.includeStatus = false;
                        }
                    }

                    // cover the case that only selects default Body field
                    else if (plainObject.configs.has('bodyColumnName')) {
                        this.callTransform.includeBody = true;
                        settings.responseBodyColName.expression = plainObject.configs
                            .get('bodyColumnName')
                            .substring(1, plainObject.configs.get('bodyColumnName').length - 1);
                        this.callTransform.staticBodyColumn = Owned.create<DataFlowColumn>(
                            DataFlowColumn.MODEL,
                            this.callTransform
                        );
                        this.callTransform.staticBodyColumn.name = settings.responseBodyColName.expression;
                    }

                    settings = adapter.setOutputColumns(this.callTransform, settings, this._handledConfigs);
                    this.callTransform.source.datasetSettings = settings;
                } else {
                    // Assumption is SQL adapter
                    const modelName = (await getServiceInstance<DataflowFormatRegistryService>(contextData, 'DataflowFormatRegistryService'))
                        .getInlineSettingsModelName(storeType);
                    const adapter = AdapterContext.defaultContextOf(contextData).dataflowDSLAdapterContext.adapterFor(
                        Metamodel.modelByName(modelName)
                    ) as BaseSqlFormatAdapter;
                    this.callTransform.source.datasetSettings = await adapter.toModel(
                        plainObject.configs,
                        true,
                        this.callTransform.source,
                        this._handledConfigs
                    );
                }
                this._handledConfigs.push(key);
            } else if (key === 'format') {
                if (plainObject.configs.get('store') !== 'restservice') {
                    this.callTransform.source.format = DataFlowExpressionUtils.extractStringConfigValue(value);
                }
            } else if (['allowSchemaDrift', 'inferDriftedColumnTypes'].includes(key)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (this.callTransform.source as any)[key] = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    value,
                    this.callTransform.source,
                    BooleanDataFlowType.TYPE
                );
                this._handledConfigs.push(key);
            } else if (key === 'dateFormats') {
                this.callTransform.source.dataTypeSettings.dateFormats =
                    DataFlowExpressionUtils.parseStringArrayInDsl(value);
                this._handledConfigs.push(key);
            } else if (key === 'timestampFormats') {
                this.callTransform.source.dataTypeSettings.timestampFormats =
                    DataFlowExpressionUtils.parseStringArrayInDsl(value).map((value) =>
                        DataFlowExpressionUtils.unescapeSingleQuoteInDslString(value)
                    );
                this._handledConfigs.push(key);
            } else if (key === 'preferredIntegralType') {
                this.callTransform.source.dataTypeSettings.preferredIntegralType =
                    DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(value);
                this._handledConfigs.push(key);
            } else if (key === 'preferredFractionalType') {
                this.callTransform.source.dataTypeSettings.preferredFractionalType =
                    DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(value);
                this._handledConfigs.push(key);
            } else if (key === 'booleanFormat') {
                const booleanFormat = DataFlowExpressionUtils.parseStringArrayInDsl(value);
                this.callTransform.source.dataTypeSettings.booleanTrueFormat = booleanFormat[0];
                this.callTransform.source.dataTypeSettings.booleanFalseFormat = booleanFormat[1];
                this._handledConfigs.push(key);
            } else if (key === 'previewRequest') {
                this.callTransform.previewRequest = true;
            }
        }
        if (plainObject.columns) {
            for (const column of plainObject.columns) {
                if (column.classType === ExpressionColumnPlainObject.classType) {
                    const expressionPlainObjectColumn = column as ExpressionColumnPlainObject;
                    const newColumn = expressionPlainObjectColumn.transient
                        ? this.transformBase.addTransientColumn()
                        : this.transformBase.addColumn();
                    newColumn.column.name = expressionPlainObjectColumn.name;
                    newColumn.dataFlowExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(
                        expressionPlainObjectColumn.expression
                    );
                    newColumn.fixedReturnTypeExpression.expression =
                        DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(
                            expressionPlainObjectColumn.typeExpression
                        );
                    newColumn.column.isTransient = expressionPlainObjectColumn.transient;
                } else if (column.classType === ExpressionColumnPatternPlainObject.classType) {
                    DataFlowSchemaAdapterUtils.addColumnPatternToTransform(
                        this.transformBase,
                        column as ExpressionColumnPatternPlainObject
                    );
                }
            }
        }

        super.postConvert();
        return this.transformation;
    }
}
